import { DBReceipt } from "../types/receipt";
import { Money } from "../types/money";
import { ProductType } from "../types/enums/ProductType";
import { PaymentMethodType } from "../types/enums/PaymentMethodType";
import { EndOfDayPaymentMethodMergeMode } from "../types/enums/EndOfDayPaymentMethodMergeMode";
import { PaymentReportGrouping } from "../types/enums/PaymentReportGrouping";
import { PaymentProvider } from "../types/enums/PaymentProvider";
import { DBStore } from "../types/store";
import { mapPaymentMethodTypeToPaymentProvider } from "./paymentProvider";
import { Currency } from "../types/enums/currency";
import {
  absMoney,
  addMoney,
  multiplyMoney,
  roundMoney,
  subtractMoney,
  zeroMoney,
} from "./money";
import {
  BookkeepingAccountId,
  ModifierId,
  PaymentMethodId,
  ProductGroupId,
  ProductId,
  ProductVariantId,
  SupplierId,
} from "../types/ids";
import { DiscountType } from "../types/enums/DiscountType";
import { ReceiptType } from "../types/enums/receiptType";
import { AppliedOffer } from "../types/offer";
import { DBDiscount } from "../types/discount";

export type LineItemExtraField = {
  key: string;
  value: string;
  name: string;
};

export interface LineItem {
  grossAmount: Money;
  vatAmount: Money;
  netAmount: Money;
  discountAmount: Money;
  bookkeepingAccountId: BookkeepingAccountId | null;
  vatRate: number;
  groupId: ProductGroupId | null;
  supplierId: SupplierId | null;
  productId: ProductId | ModifierId;
  productName: string | null;
  productType: ProductType;
  count: number;
  costcenter?: string;
  unitName?: string;
  variantId?: ProductVariantId | null;
  variantName?: string | null;
  articleNumber?: string | null;
  extraFields?: LineItemExtraField[] | null;
  isTakeaway: boolean;
  appliedOffer?: AppliedOffer | null;
}

type PendingLineItem = {
  originalGrossAmount: Money;
  remainingGrossAmount: Money;
  roundGross: boolean;
  bookkeepingAccountId: BookkeepingAccountId | null;
  vatRate: number;
  groupId: ProductGroupId | null;
  supplierId: SupplierId | null;
  productId: ProductId | ModifierId;
  productName: string | null;
  productType: ProductType;
  count: number;
  costcenter?: string;
  unitName?: string;
  variantId?: ProductVariantId | null;
  variantName?: string | null;
  articleNumber?: string | null;
  extraFields?: LineItemExtraField[] | null;
  isTakeaway: boolean;
  noDiscount?: boolean;
  appliedOffer?: AppliedOffer | null;
};

const applyItemDiscount = (
  grossAmount: Money,
  discount: DBDiscount | null | undefined,
  applyAmount: boolean,
): Money => {
  if (!discount) {
    return grossAmount;
  }
  if (discount.type === DiscountType.PERCENTAGE) {
    if (applyAmount) {
      return multiplyMoney(grossAmount, 1 - discount.percentage / 100);
    }
    const itemDiscountAmount = roundMoney(
      multiplyMoney(grossAmount, discount.percentage / 100),
    );
    return subtractMoney(grossAmount, itemDiscountAmount);
  }
  if (applyAmount) {
    return subtractMoney(grossAmount, discount.amount);
  }
  return grossAmount;
};

const applyReceiptPercentage = (
  pending: PendingLineItem,
  percentage: number,
) => {
  if (pending.noDiscount) {
    return;
  }
  if (pending.roundGross) {
    pending.remainingGrossAmount = multiplyMoney(
      pending.remainingGrossAmount,
      1 - percentage / 100,
    );
    return;
  }
  const receiptDiscountAmount = roundMoney(
    multiplyMoney(pending.remainingGrossAmount, percentage / 100),
  );
  pending.remainingGrossAmount = subtractMoney(
    pending.remainingGrossAmount,
    receiptDiscountAmount,
  );
};

// A positive amount is spread over the positive lines. A negative amount is a
// refund's share of the sale's discount and is spread over the negative lines.
const applyReceiptAmount = (pending: PendingLineItem[], amount: Money) => {
  const sign = Math.sign(amount.amount);
  const discountedLines = pending.filter(
    (line) => line.remainingGrossAmount.amount * sign > 0 && !line.noDiscount,
  );
  const totalDiscounted = discountedLines.reduce(
    (sum, line) => sum + Math.abs(line.remainingGrossAmount.amount),
    0,
  );
  if (totalDiscounted <= 0 || sign === 0) {
    return;
  }
  const amountToAllocate = Math.min(Math.abs(amount.amount), totalDiscounted);
  let leftover = amountToAllocate;
  for (let k = 0; k < discountedLines.length; k++) {
    const line = discountedLines[k];
    if (!line) {
      continue;
    }
    const remaining = Math.abs(line.remainingGrossAmount.amount);
    const isLast = k === discountedLines.length - 1;
    let share: number;
    if (isLast) {
      share = Math.min(leftover, remaining);
    } else {
      share = Math.round((amountToAllocate * remaining) / totalDiscounted);
      share = Math.min(share, remaining, leftover);
    }
    leftover -= share;
    line.remainingGrossAmount = subtractMoney(
      line.remainingGrossAmount,
      share * sign,
    );
  }
};

export const convertReceiptToLineItems = (
  receipt: Omit<
    DBReceipt,
    | "id"
    | "createdAt"
    | "company"
    | "type"
    | "payments"
    | "rounding"
    | "refundReceipts"
    | "total"
    | "chainId"
    | "storeId"
  >,
  _currency: Currency,
) => {
  const pending: PendingLineItem[] = [];

  for (const item of receipt.items) {
    if (item.type === ProductType.TEXT) {
      continue;
    }
    const count = item.quantity * item.sizeOfItem;
    const originalGrossAmount = multiplyMoney(item.unitPrice, count);
    const noDiscount = item.noDiscount === true;
    const remainingGrossAmount = applyItemDiscount(
      originalGrossAmount,
      noDiscount ? null : item.discount,
      true,
    );
    const isTakeaway = item.isTakeaway === true;
    pending.push({
      originalGrossAmount,
      remainingGrossAmount,
      roundGross: true,
      bookkeepingAccountId: item.bookkeepingAccountId,
      vatRate: item.VAT,
      groupId: item.groupId,
      supplierId: item.supplierId ?? null,
      productId: item.productId,
      productName: item.name,
      productType: item.type,
      count,
      costcenter: item.bookkeepingCostcenter,
      unitName: item.unit?.name,
      variantId: item.variantId ?? null,
      variantName: item.variantName ?? null,
      articleNumber: item.articleNumber ?? null,
      extraFields: item.extraFields ?? null,
      isTakeaway,
      noDiscount,
      appliedOffer: noDiscount ? undefined : (item.appliedOffer ?? undefined),
    });

    for (const modifier of item.modifiers ?? []) {
      if (modifier.unitPrice.amount === 0) {
        continue;
      }
      const modifierCount =
        item.quantity * item.sizeOfItem * (modifier.quantity ?? 1);
      const modifierOriginalGrossAmount = multiplyMoney(
        modifier.unitPrice,
        modifierCount,
      );
      pending.push({
        originalGrossAmount: modifierOriginalGrossAmount,
        remainingGrossAmount: applyItemDiscount(
          modifierOriginalGrossAmount,
          noDiscount ? null : item.discount,
          false,
        ),
        roundGross: false,
        bookkeepingAccountId:
          modifier.bookkeepingAccountId ?? item.bookkeepingAccountId,
        vatRate: modifier.VAT,
        groupId: item.groupId,
        supplierId: item.supplierId ?? null,
        productId: modifier.modifierId,
        productName: modifier.name,
        productType: item.type,
        count: modifierCount,
        unitName: item.unit?.name,
        variantId: null,
        variantName: null,
        articleNumber: null,
        extraFields: null,
        isTakeaway,
        noDiscount,
      });
    }
  }

  if (receipt.discount?.type === DiscountType.PERCENTAGE) {
    for (const line of pending) {
      applyReceiptPercentage(line, receipt.discount.percentage);
    }
  } else if (receipt.discount?.type === DiscountType.AMOUNT) {
    applyReceiptAmount(pending, receipt.discount.amount);
  }

  return pending.map((line) => {
    const grossAmount = line.roundGross
      ? roundMoney(line.remainingGrossAmount)
      : line.remainingGrossAmount;
    const discountAmount = subtractMoney(
      line.originalGrossAmount,
      line.roundGross ? grossAmount : line.remainingGrossAmount,
    );
    const vatAmount = calculateVatAmount(
      line.remainingGrossAmount,
      line.vatRate,
    );
    const lineItem: LineItem = {
      grossAmount,
      vatAmount: roundMoney(vatAmount),
      netAmount: roundMoney(
        subtractMoney(line.remainingGrossAmount, vatAmount),
      ),
      discountAmount,
      bookkeepingAccountId: line.bookkeepingAccountId,
      vatRate: line.vatRate,
      groupId: line.groupId,
      supplierId: line.supplierId,
      productId: line.productId,
      productName: line.productName,
      productType: line.productType,
      count: line.count,
      costcenter: line.costcenter,
      unitName: line.unitName,
      variantId: line.variantId,
      variantName: line.variantName,
      articleNumber: line.articleNumber,
      extraFields: line.extraFields,
      isTakeaway: line.isTakeaway,
    };
    if (line.appliedOffer) {
      lineItem.appliedOffer = line.appliedOffer;
    }
    return lineItem;
  });
};

export const calculateTotalSalesAmount = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce(
    (acc, item) => addMoney(acc, item.grossAmount),
    zeroMoney(currency),
  );
};

export const calculateTotalNetAmount = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return subtractMoney(
    calculateTotalSalesAmount(lineItems, currency),
    calculateTotalVat(lineItems, currency),
  );
};

export const NO_GROUP_ID = "NO_GROUP";
export const GIFT_CARD_GROUP_ID = "GIFT_CARD";
export const SERVICE_ORDER_DEPOSIT_GROUP_ID = "SERVICE_ORDER_DEPOSIT";

export const reportGroupIdForLineItem = (
  item: Pick<LineItem, "groupId" | "productType">,
): string => {
  if (item.productType === ProductType.GIFT_CARD) {
    return GIFT_CARD_GROUP_ID;
  }
  if (item.productType === ProductType.SERVICE_ORDER_DEPOSIT) {
    return SERVICE_ORDER_DEPOSIT_GROUP_ID;
  }
  return item.groupId ?? NO_GROUP_ID;
};

export const salesByMainGroupKey = (
  groupId: string | null,
  isTakeaway: boolean,
) => `${groupId ?? NO_GROUP_ID}:${isTakeaway ? "1" : "0"}`;

export const formatSalesByMainGroupName = (
  groupName: string,
  isTakeaway: boolean,
  takeawayLabel: string,
) => (isTakeaway === true ? `${groupName} (${takeawayLabel})` : groupName);

export const sortSalesByMainGroups = <
  T extends { groupName: string; isTakeaway?: boolean },
>(
  groups: readonly T[],
): T[] =>
  [...groups].sort((leftGroup, rightGroup) => {
    const nameComparison = leftGroup.groupName.localeCompare(
      rightGroup.groupName,
    );
    if (nameComparison !== 0) {
      return nameComparison;
    }
    return (
      Number(leftGroup.isTakeaway === true) -
      Number(rightGroup.isTakeaway === true)
    );
  });

export const roundCount = (count: number) => Math.round(count * 1000) / 1000;

export const calculateSalesByMainGroup = (
  lineItems: LineItem[],
  roundCounts = true,
) => {
  const groups = lineItems.reduce(
    (acc, item) => {
      const groupId = reportGroupIdForLineItem(item);
      const key = salesByMainGroupKey(groupId, item.isTakeaway);
      const existing = acc.get(key);
      acc.set(
        key,
        existing
          ? {
              groupId: existing.groupId,
              isTakeaway: existing.isTakeaway,
              grossAmount: addMoney(existing.grossAmount, item.grossAmount),
              vatAmount: addMoney(existing.vatAmount, item.vatAmount),
              netAmount: addMoney(existing.netAmount, item.netAmount),
              itemCount: existing.itemCount + item.count,
            }
          : {
              groupId,
              isTakeaway: item.isTakeaway,
              grossAmount: item.grossAmount,
              vatAmount: item.vatAmount,
              netAmount: item.netAmount,
              itemCount: item.count,
            },
      );
      return acc;
    },
    new Map<
      string,
      {
        groupId: string;
        isTakeaway: boolean;
        grossAmount: Money;
        vatAmount: Money;
        netAmount: Money;
        itemCount: number;
      }
    >(),
  );
  if (roundCounts) {
    for (const group of groups.values()) {
      group.itemCount = roundCount(group.itemCount);
    }
  }
  return groups;
};

export const calculateTotalVat = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce(
    (acc, item) => addMoney(acc, item.vatAmount),
    zeroMoney(currency),
  );
};

export const calculateVatByRate = (lineItems: LineItem[]) => {
  return lineItems.reduce(
    (acc, item) => {
      const existing = acc.get(item.vatRate);
      acc.set(
        item.vatRate,
        existing
          ? {
              vatAmount: addMoney(existing.vatAmount, item.vatAmount),
              netAmount: addMoney(existing.netAmount, item.netAmount),
              grossAmount: addMoney(existing.grossAmount, item.grossAmount),
            }
          : {
              vatAmount: item.vatAmount,
              netAmount: item.netAmount,
              grossAmount: item.grossAmount,
            },
      );
      return acc;
    },
    new Map<
      number,
      {
        vatAmount: Money;
        netAmount: Money;
        grossAmount: Money;
      }
    >(),
  );
};

export const calculateSoldGoodsCount = (
  lineItems: LineItem[],
  roundCounts = true,
) => {
  const count = lineItems.reduce((acc, item) => {
    if (item.productType === ProductType.PRODUCT) {
      return acc + item.count;
    }
    return acc;
  }, 0);
  return roundCounts ? roundCount(count) : count;
};

export const calculateSoldServicesCount = (
  lineItems: LineItem[],
  roundCounts = true,
) => {
  const count = lineItems.reduce((acc, item) => {
    if (item.productType === ProductType.SERVICE) {
      return acc + item.count;
    }
    return acc;
  }, 0);
  return roundCounts ? roundCount(count) : count;
};

export const calculateSalesByPaymentMethod = (receipts: DBReceipt[]) => {
  const paymentMethodMap = new Map<
    PaymentMethodId,
    {
      name?: string;
      amount: Money;
      type: PaymentMethodType;
    }
  >();
  for (const receipt of receipts) {
    for (const payment of receipt.payments) {
      const existing = paymentMethodMap.get(payment.paymentMethodId);
      paymentMethodMap.set(
        payment.paymentMethodId,
        existing
          ? {
              name: existing.name,
              amount: addMoney(existing.amount, payment.amount),
              type: existing.type,
            }
          : {
              name: payment.name,
              amount: payment.amount,
              type: payment.type,
            },
      );
    }
  }
  return Array.from(paymentMethodMap.entries()).map(([id, paymentMethod]) => ({
    paymentMethodId: id,
    paymentMethodType: paymentMethod.type,
    name: paymentMethod.name,
    amount: paymentMethod.amount,
  }));
};

const CARD_TERMINAL_PAYMENT_METHOD_TYPES = [
  PaymentMethodType.NETS_MOCK,
  PaymentMethodType.NETS_TERMINAL,
  PaymentMethodType.WESTPAY_CLOUD,
  PaymentMethodType.VIVA,
  PaymentMethodType.VIVA_CLOUD,
  PaymentMethodType.VIVA_SMART_CHECKOUT,
  PaymentMethodType.SOFTPAY,
  PaymentMethodType.MARKETPAY_CLOUD,
  PaymentMethodType.MARKETPAY_LOCAL,
  PaymentMethodType.MARKETPAY_SDK,
];

const SWISH_PAYMENT_METHOD_TYPES = [
  PaymentMethodType.SWISH_COMPANY,
  PaymentMethodType.SWISH_COMMERCE,
];

export type PaymentReportGroup =
  | { by: "type"; value: PaymentMethodType }
  | { by: "provider"; value: PaymentProvider }
  | { by: "category"; value: "CARD" | "SWISH" };

export const resolvePaymentReportGrouping = (
  store: Pick<
    DBStore,
    "paymentReportGrouping" | "endOfDayPaymentMethodMergeMode"
  >,
) => {
  if (store.paymentReportGrouping) {
    return store.paymentReportGrouping;
  }
  return !store.endOfDayPaymentMethodMergeMode ||
    store.endOfDayPaymentMethodMergeMode === EndOfDayPaymentMethodMergeMode.NONE
    ? PaymentReportGrouping.METHOD
    : PaymentReportGrouping.TYPE;
};

const paymentReportGroupOf = (
  grouping: PaymentReportGrouping,
  type: PaymentMethodType,
): PaymentReportGroup | null => {
  if (
    grouping === PaymentReportGrouping.METHOD ||
    type === PaymentMethodType.OTHER
  ) {
    return null;
  }
  if (grouping === PaymentReportGrouping.CATEGORY) {
    if (CARD_TERMINAL_PAYMENT_METHOD_TYPES.includes(type)) {
      return { by: "category", value: "CARD" };
    }
    if (SWISH_PAYMENT_METHOD_TYPES.includes(type)) {
      return { by: "category", value: "SWISH" };
    }
    return { by: "type", value: type };
  }
  const provider = mapPaymentMethodTypeToPaymentProvider(type);
  if (grouping === PaymentReportGrouping.PROVIDER && provider) {
    return { by: "provider", value: provider };
  }
  return { by: "type", value: type };
};

const PAYMENT_REPORT_GROUP_KEY_PREFIX = {
  type: "paymentMethodType",
  provider: "paymentProvider",
  category: "paymentCategory",
} as const;

// Unique per group and usable as a translation key in app and admin
export const paymentReportGroupKey = (group: PaymentReportGroup) =>
  `${PAYMENT_REPORT_GROUP_KEY_PREFIX[group.by]}.${group.value}`;

const groupPaymentRows = <T>(
  rows: { id: string; type: PaymentMethodType | undefined; row: T }[],
  grouping: PaymentReportGrouping,
  merge: (group: PaymentReportGroup, row: T, existing: T | undefined) => T,
) => {
  const groups = new Map<string, T>();
  for (const { id, type, row } of rows) {
    const group = type ? paymentReportGroupOf(grouping, type) : null;
    if (!group) {
      groups.set(`id:${id}`, row);
      continue;
    }
    const key = paymentReportGroupKey(group);
    groups.set(key, merge(group, row, groups.get(key)));
  }
  return Array.from(groups.values());
};

export const groupSalesByPaymentMethod = (
  salesByPaymentMethod: ReturnType<typeof calculateSalesByPaymentMethod>,
  grouping: PaymentReportGrouping,
  groupName: (group: PaymentReportGroup) => string,
) =>
  groupPaymentRows<{
    paymentMethodType: string;
    amount: Money;
    paymentMethodId?: PaymentMethodId;
    name?: string;
  }>(
    salesByPaymentMethod.map((payment) => ({
      id: payment.paymentMethodId,
      type: payment.paymentMethodType,
      row: payment,
    })),
    grouping,
    (group, payment, existing) => ({
      paymentMethodType: group.value,
      amount: existing
        ? addMoney(existing.amount, payment.amount)
        : payment.amount,
      name: existing?.name ?? groupName(group),
    }),
  );

export const groupPaymentMethodTotals = (
  totals: Record<
    string,
    {
      count: number;
      grossAmount: Money;
      name?: string;
      type?: PaymentMethodType;
    }
  >,
  grouping: PaymentReportGrouping,
  typeOf: (id: string) => PaymentMethodType | undefined,
  groupName: (group: PaymentReportGroup) => string,
) =>
  groupPaymentRows<{
    key: string;
    name?: string;
    count: number;
    grossAmount: Money;
  }>(
    Object.entries(totals).map(([id, total]) => ({
      id,
      type: total.type ?? typeOf(id),
      row: {
        key: id,
        name: total.name,
        count: total.count,
        grossAmount: total.grossAmount,
      },
    })),
    grouping,
    (group, total, existing) => ({
      key: paymentReportGroupKey(group),
      name: existing?.name ?? groupName(group),
      count: (existing?.count ?? 0) + total.count,
      grossAmount: existing
        ? addMoney(existing.grossAmount, total.grossAmount)
        : total.grossAmount,
    }),
  );

export const isPayoutReceipt = (receipt: Pick<DBReceipt, "type">) =>
  receipt.type === ReceiptType.PAYOUT;

export const excludePayoutReceipts = <T extends Pick<DBReceipt, "type">>(
  receipts: T[],
) => receipts.filter((receipt) => !isPayoutReceipt(receipt));

export const calculatePayoutSummary = (
  receipts: DBReceipt[],
  currency: Currency,
) => {
  const payoutReceipts = receipts.filter(isPayoutReceipt);
  const byPaymentMethod = calculateSalesByPaymentMethod(payoutReceipts).map(
    (payment) => ({
      ...payment,
      amount: absMoney(payment.amount),
    }),
  );
  return {
    count: payoutReceipts.length,
    amount: byPaymentMethod.reduce(
      (acc, payment) => addMoney(acc, payment.amount),
      zeroMoney(currency),
    ),
    byPaymentMethod,
  };
};

export const hasSoldLines = (
  receipt: Parameters<typeof convertReceiptToLineItems>[0],
  currency: Currency,
) =>
  convertReceiptToLineItems(receipt, currency).some(
    (lineItem) => lineItem.grossAmount.amount > 0,
  );

export const calculateCashPayouts = (
  receipts: Pick<DBReceipt, "type" | "payments">[],
  currency: Currency,
) =>
  receipts
    .filter(isPayoutReceipt)
    .flatMap((receipt) => receipt.payments)
    .filter((payment) => payment.type === PaymentMethodType.CASH)
    .reduce(
      (acc, payment) => addMoney(acc, payment.amount),
      zeroMoney(currency),
    );

export const getReceiptDisplayNumber = (
  receipt: Pick<DBReceipt, "receiptNumber" | "purchaseNoteNumber">,
) => receipt.receiptNumber ?? receipt.purchaseNoteNumber;

export const calculateTotalDiscounts = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce((acc, item) => {
    return addMoney(acc, item.discountAmount);
  }, zeroMoney(currency));
};

export const getReceiptLevelDiscountBreakdown = (
  receipt: Parameters<typeof convertReceiptToLineItems>[0],
  currency: Currency,
): {
  subtotalBeforeCartDiscount: Money;
  receiptLevelDiscountAmount: Money;
} | null => {
  if (!receipt.discount) {
    return null;
  }
  const withDiscount = convertReceiptToLineItems(receipt, currency);
  const withoutDiscount = convertReceiptToLineItems(
    { ...receipt, discount: undefined },
    currency,
  );
  return {
    subtotalBeforeCartDiscount: calculateTotalSalesAmount(
      withoutDiscount,
      currency,
    ),
    receiptLevelDiscountAmount: subtractMoney(
      calculateTotalDiscounts(withDiscount, currency),
      calculateTotalDiscounts(withoutDiscount, currency),
    ),
  };
};

// SWEDISH END OF DAY REPORT
export const calculateGrandTotalGross = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce((acc, item) => {
    if (item.grossAmount.amount >= 0) {
      return addMoney(acc, addMoney(item.grossAmount, item.discountAmount));
    }
    return acc;
  }, zeroMoney(currency));
};

// SWEDISH END OF DAY REPORT
export const calculateGrandTotalRefunds = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce((acc, item) => {
    if (item.grossAmount.amount < 0) {
      return addMoney(acc, absMoney(item.grossAmount));
    } else {
      return addMoney(acc, item.discountAmount);
    }
  }, zeroMoney(currency));
};

// SWEDISH END OF DAY REPORT
export const calculateGrandTotalNet = (
  lineItems: LineItem[],
  currency: Currency,
) => {
  return lineItems.reduce((acc, item) => {
    return addMoney(acc, item.grossAmount);
  }, zeroMoney(currency));
};

// Helper function to calculate VAT amount from a gross amount and VAT rate
export const calculateVatAmount = (
  grossAmount: Money,
  vatRate: number,
): Money => {
  return roundMoney(multiplyMoney(grossAmount, vatRate / (100 + vatRate)));
};
